import { Box, Container, Typography } from "@mui/material";
import React, { useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import StaticPageLayout from "../../components/StaticPageLayout";
import * as styles from "./styles";

type TranslateFnT = (key: string, options?: Record<string, unknown>) => string;

type ValueItemT = {
  number: string;
  label: string;
};

type LeaderT = {
  name: string;
  title: string;
  bio: string;
  image?: string;
};

type DirectorT = {
  name: string;
  title: string;
  image?: string;
};

const useInView = (threshold = 0.15) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) setInView(true);
      },
      { threshold },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return { containerRef, inView };
};

const getInitials = (name: string) => {
  const parts = name.trim().split(" ");
  if (parts.length < 2) return parts[0]?.[0] ?? "";
  return `${parts[0][0]}${parts[parts.length - 1][0]}`;
};

type CEOSpotlightSectionPropsT = { inView: boolean; t: TranslateFnT };

const CEOSpotlightSection = ({ inView, t }: CEOSpotlightSectionPropsT) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: { xs: "column", md: "row" },
      background: "rgba(255,255,255,0.02)",
      borderTop: "1px solid rgba(56,189,248,0.08)",
      borderBottom: "1px solid rgba(56,189,248,0.08)",
      overflow: "hidden",
      opacity: inView ? 1 : 0,
      transform: inView ? "none" : "translateY(40px)",
      transition: "opacity 0.9s ease, transform 0.9s ease",
    }}
  >
    <Box sx={styles.ceoSpotlightContainer}>
      <Typography component="h2" sx={styles.ceoName}>
        {t("ceo.name")}
      </Typography>
      <Typography sx={styles.ceoTitle}>{t("ceo.title")}</Typography>
      <Box sx={styles.ceoDivider} />
      <Typography sx={styles.ceoBio}>{t("ceo.bio")}</Typography>
    </Box>

    <Box sx={styles.ceoImageContainer}>
      <Box component="img" src="/img/assets/JB-hs.jpg" alt={t("ceo.imageAlt")} sx={styles.ceoImage} />
      <Box sx={styles.ceoImageAccent} />
      <Box sx={styles.ceoImageCorner} />
    </Box>
  </Box>
);

type LeaderRowPropsT = { leader: LeaderT; index: number; inView: boolean };

const LeaderRow = ({ leader, index, inView }: LeaderRowPropsT) => {
  const [hovered, setHovered] = useState(false);
  const isEven = index % 2 === 0;
  const initials = getInitials(leader.name);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: { xs: "column", md: isEven ? "row" : "row-reverse" },
        minHeight: { md: "420px" },
        overflow: "hidden",
        opacity: inView ? 1 : 0,
        transform: inView ? "none" : "translateY(48px)",
        transition: `opacity 0.8s ease ${index * 0.12}s, transform 0.8s ease ${index * 0.12}s`,
      }}
    >
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={styles.leaderImageContainer}
      >
        {leader.image ? (
          <Box
            component="img"
            src={leader.image}
            alt={leader.name}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "center top",
              transform: hovered ? "scale(1.03)" : "scale(1)",
              transition: "transform 0.6s ease",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(135deg, #0a1628, #0d2040)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transform: hovered ? "scale(1.03)" : "scale(1)",
              transition: "transform 0.6s ease",
            }}
          >
            <Box sx={styles.leaderPlaceholderBg} />
            <Typography sx={styles.leaderPlaceholderInitials}>{initials}</Typography>
          </Box>
        )}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "1px",
            background:
              "linear-gradient(to bottom, transparent, rgba(56,189,248,0.3) 30%, rgba(56,189,248,0.3) 70%, transparent)",
            ...(isEven ? { right: 0 } : { left: 0 }),
          }}
        />
      </Box>

      <Box
        sx={{
          flex: "0 0 50%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          px: { xs: 4, md: 8, lg: 10 },
          py: { xs: 5, md: 8 },
          background: isEven ? "rgba(255,255,255,0.02)" : "transparent",
          position: "relative",
        }}
      >
        <Typography component="h3" sx={styles.leaderName}>
          {leader.name}
        </Typography>
        <Typography sx={styles.leaderTitle}>{leader.title}</Typography>
        <Box sx={styles.leaderDivider} />
        <Typography sx={styles.leaderBio}>{leader.bio}</Typography>
      </Box>
    </Box>
  );
};

type DirectorCardPropsT = { director: DirectorT; index: number; inView: boolean };

const DirectorCard = ({ director, index, inView }: DirectorCardPropsT) => {
  const [hovered, setHovered] = useState(false);
  const initials = getInitials(director.name);

  return (
    <Box sx={{ width: { xs: "100%", sm: "calc(50% - 12px)", md: "calc(33.333% - 16px)" } }}>
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          background: hovered ? "rgba(255,255,255,0.055)" : "rgba(255,255,255,0.03)",
          backdropFilter: "blur(16px)",
          border: hovered ? "1px solid rgba(56,189,248,0.28)" : "1px solid rgba(56,189,248,0.1)",
          borderRadius: "20px",
          overflow: "hidden",
          transform: hovered ? "translateY(-4px)" : "none",
          boxShadow: hovered ? "0 16px 48px rgba(0,0,0,0.35), 0 0 32px rgba(56,189,248,0.06)" : "none",
          transition: "all 0.3s ease",
          opacity: inView ? 1 : 0,
          ...(!inView && { transform: "translateY(32px)" }),
          ...(inView && {
            transition: `all 0.3s ease, opacity 0.6s ease ${index * 0.07}s, transform 0.6s ease ${index * 0.07}s`,
          }),
        }}
      >
        <Box sx={styles.directorCardImageContainer}>
          {director.image && (
            <Box component="img" src={director.image} alt={director.name} sx={styles.directorCardImage} />
          )}
          {!director.image && <Box sx={styles.directorPlaceholderBg} />}
          {!director.image && (
            <Box sx={styles.directorPlaceholderAvatar}>
              <Typography sx={styles.directorPlaceholderInitials}>{initials}</Typography>
            </Box>
          )}
        </Box>
        <Box sx={{ p: { xs: 3, md: 3.5 }, textAlign: "center" }}>
          <Typography sx={styles.directorCardName}>{director.name}</Typography>
          <Typography sx={styles.directorCardTitle}>{director.title}</Typography>
          <Box sx={styles.directorCardDivider} />
        </Box>
      </Box>
    </Box>
  );
};

type ValueCardPropsT = { value: ValueItemT; index: number; inView: boolean };

const ValueCard = ({ value, index, inView }: ValueCardPropsT) => {
  const [hovered, setHovered] = useState(false);

  return (
    <Box
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      sx={{
        position: "relative",
        overflow: "hidden",
        background: hovered ? "rgba(56,189,248,0.06)" : "rgba(255,255,255,0.03)",
        backdropFilter: "blur(12px)",
        border: hovered ? "1px solid rgba(56,189,248,0.5)" : "1px solid rgba(56,189,248,0.1)",
        borderRadius: "16px",
        p: { xs: 2.5, md: 3 },
        textAlign: "center",
        boxShadow: hovered ? "0 0 20px rgba(56,189,248,0.12)" : "none",
        transition: "all 0.25s ease",
        opacity: inView ? 1 : 0,
        transform: inView ? "none" : "translateY(24px)",
        ...(inView && {
          transition: `all 0.25s ease, opacity 0.6s ease ${index * 0.06}s, transform 0.6s ease ${index * 0.06}s`,
        }),
      }}
    >
      <Box sx={styles.valueCardGlowTopLeft} />
      <Box sx={styles.valueCardGlowBottomRight} />
      <Typography sx={styles.valueCardNumber}>{value.number}</Typography>
      <Typography sx={styles.valueCardLabel}>{value.label}</Typography>
    </Box>
  );
};

type MissionVisionValuesSectionPropsT = { inView: boolean; t: TranslateFnT };

const MissionVisionValuesSection = ({ inView, t }: MissionVisionValuesSectionPropsT) => {
  const powurValues: ValueItemT[] = [
    { number: t("values.items.v01.number"), label: t("values.items.v01.label") },
    { number: t("values.items.v02.number"), label: t("values.items.v02.label") },
    { number: t("values.items.v03.number"), label: t("values.items.v03.label") },
    { number: t("values.items.v04.number"), label: t("values.items.v04.label") },
    { number: t("values.items.v05.number"), label: t("values.items.v05.label") },
    { number: t("values.items.v06.number"), label: t("values.items.v06.label") },
    { number: t("values.items.v07.number"), label: t("values.items.v07.label") },
    { number: t("values.items.v08.number"), label: t("values.items.v08.label") },
    { number: t("values.items.v09.number"), label: t("values.items.v09.label") },
    { number: t("values.items.v10.number"), label: t("values.items.v10.label") },
  ];

  return (
    <Box sx={{ position: "relative", overflow: "hidden", py: { xs: 12, md: 18 } }}>
      <Box sx={styles.missionSectionBgImage} />
      <Box sx={styles.missionSectionOverlay} />
      <Box sx={styles.missionSectionTopGradient} />
      <Box sx={styles.missionSectionBottomGradient} />
      <Container maxWidth="xl" sx={{ position: "relative", zIndex: 1 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 8, md: 0 },
            opacity: inView ? 1 : 0,
            transform: inView ? "none" : "translateY(40px)",
            transition: "opacity 0.9s ease, transform 0.9s ease",
            mb: { xs: 12, md: 18 },
          }}
        >
          <Box sx={{ flex: 1, pr: { md: 8 } }}>
            <Typography sx={{ ...styles.eyebrowSx, mb: 1.5 }}>{t("mission.visionEyebrow")}</Typography>
            <Box sx={styles.visionMissionDividerLine} />
            <Typography sx={styles.visionMissionBody}>{t("mission.visionBody")}</Typography>
          </Box>

          <Box sx={styles.verticalDivider} />

          <Box sx={{ flex: 1, pl: { md: 8 } }}>
            <Typography sx={{ ...styles.eyebrowSx, mb: 1.5 }}>{t("mission.missionEyebrow")}</Typography>
            <Box sx={styles.visionMissionDividerLine} />
            <Typography sx={styles.visionMissionBody}>{t("mission.missionBody")}</Typography>
          </Box>
        </Box>

        <Box
          sx={{
            textAlign: "center",
            mb: { xs: 6, md: 8 },
            opacity: inView ? 1 : 0,
            transform: inView ? "none" : "translateY(24px)",
            transition: "opacity 0.9s ease 0.2s, transform 0.9s ease 0.2s",
          }}
        >
          <Typography sx={{ ...styles.eyebrowSx, mb: 1.5 }}>{t("values.eyebrow")}</Typography>
          <Box sx={styles.valuesHeadingLine} />
          <Typography sx={styles.valuesSubtext}>{t("values.subtext")}</Typography>
        </Box>

        <Box sx={styles.valuesGrid}>
          {powurValues.map((value, i) => (
            <ValueCard key={value.number} value={value} index={i} inView={inView} />
          ))}
        </Box>

        <Box
          sx={{
            textAlign: "center",
            mt: { xs: 12, md: 16 },
            opacity: inView ? 1 : 0,
            transform: inView ? "none" : "translateY(24px)",
            transition: "opacity 0.9s ease 0.6s, transform 0.9s ease 0.6s",
          }}
        >
          <Typography sx={styles.cultureEyebrow}>{t("culture.eyebrow")}</Typography>
          <Typography component="h3" sx={styles.cultureHeading}>
            {t("culture.heading")}
            <Box component="span" sx={styles.gradientTextSx}>
              {t("culture.headingGradient")}
            </Box>
          </Typography>
          <Box component="a" href="/join" sx={styles.joinButton}>
            {t("culture.joinButton")}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

const AboutContent = () => {
  const { t } = useTranslation("static_about");
  const { containerRef: heroRef, inView: heroInView } = useInView(0.1);
  const { containerRef: leadershipRef, inView: leadershipInView } = useInView(0.05);
  const { containerRef: ceoRef, inView: ceoInView } = useInView(0.1);
  const { containerRef: directorsRef, inView: directorsInView } = useInView(0.1);
  const { containerRef: missionRef, inView: missionInView } = useInView(0.1);

  const leaders: LeaderT[] = [
    {
      name: t("leaders.mikeLindmark.name"),
      title: t("leaders.mikeLindmark.title"),
      bio: t("leaders.mikeLindmark.bio"),
      image: "/img/assets/MikeLindmark-hs.jpg",
    },
    {
      name: t("leaders.danielDiFiore.name"),
      title: t("leaders.danielDiFiore.title"),
      bio: t("leaders.danielDiFiore.bio"),
      image: "/img/assets/DanielDiFiore-hs.jpg",
    },
    {
      name: t("leaders.jerryCarpenter.name"),
      title: t("leaders.jerryCarpenter.title"),
      bio: t("leaders.jerryCarpenter.bio"),
      image: "/img/assets/JerryCarpenter-hs.jpg",
    },
    {
      name: t("leaders.nickPhillips.name"),
      title: t("leaders.nickPhillips.title"),
      bio: t("leaders.nickPhillips.bio"),
      image: "/img/assets/NickPhillips-hs.jpg",
    },
  ];

  const directors: DirectorT[] = [
    {
      name: t("directors.bobbySmith.name"),
      title: t("directors.bobbySmith.title"),
      image: "/img/assets/BobbySmith-hs.jpg",
    },
    {
      name: t("directors.paulKitchen.name"),
      title: t("directors.paulKitchen.title"),
      image: "/img/assets/PaulKitchen-hs.jpg",
    },
    {
      name: t("directors.bethRobinson.name"),
      title: t("directors.bethRobinson.title"),
      image: "/img/assets/BethRobinson-hs.jpg",
    },
    {
      name: t("directors.markRomero.name"),
      title: t("directors.markRomero.title"),
      image: "/img/assets/MarkRomero-hs.jpg",
    },
    {
      name: t("directors.meganCallihan.name"),
      title: t("directors.meganCallihan.title"),
      image: "/img/assets/MeganCallihan-hs.jpg",
    },
    {
      name: t("directors.andyBoyd.name"),
      title: t("directors.andyBoyd.title"),
      image: "/img/assets/AndyBoyd-hs.jpg",
    },
    {
      name: t("directors.emilyBudd.name"),
      title: t("directors.emilyBudd.title"),
      image: "/img/assets/EmilyBudd-hs.jpg",
    },
    {
      name: t("directors.donaldLuman.name"),
      title: t("directors.donaldLuman.title"),
      image: "/img/assets/DonaldLuman-hs.png",
    },
    {
      name: t("directors.marineHanano.name"),
      title: t("directors.marineHanano.title"),
      image: "/img/assets/MarineHanano-hs.png",
    },
    {
      name: t("directors.raeRint.name"),
      title: t("directors.raeRint.title"),
      image: "/img/assets/RaeRint-hs.jpg",
    },
    {
      name: t("directors.kimberlyWhitfield.name"),
      title: t("directors.kimberlyWhitfield.title"),
      image: "/img/assets/KimberlyWhitfield-hs.jpg",
    },
    {
      name: t("directors.grahamHorne.name"),
      title: t("directors.grahamHorne.title"),
      image: "/img/assets/GrahamHorne-hs.jpg",
    },
    {
      name: t("directors.jenniferBrantley.name"),
      title: t("directors.jenniferBrantley.title"),
      image: "/img/assets/JenniferBrantley.jpg",
    },
  ];

  return (
    <>
      <Box sx={styles.heroSection}>
        <Box sx={styles.heroBgImage} />
        <Box sx={styles.heroOverlay} />
        <Container
          maxWidth="xl"
          ref={heroRef}
          sx={{
            display: "flex",
            justifyContent: { xs: "flex-start", md: "flex-end" },
            pr: { md: 8, lg: 16 },
            py: { xs: 16, md: 20 },
          }}
        >
          <Box
            sx={{
              maxWidth: { xs: "100%", md: 720 },
              textAlign: "left",
              position: "relative",
              zIndex: 1,
              opacity: heroInView ? 1 : 0,
              transform: heroInView ? "none" : "translateY(40px)",
              transition: "opacity 0.9s ease, transform 0.9s ease",
            }}
          >
            <Typography sx={{ ...styles.eyebrowSx, mb: 3 }}>{t("hero.eyebrow")}</Typography>
            <Typography component="h1" sx={styles.heroHeading}>
              {t("hero.headlineBase")}
              <Box component="span" sx={styles.heroGradientText}>
                {t("hero.headlineGradient")}
              </Box>
              {t("hero.headlineSuffix")}
            </Typography>
            <Typography sx={styles.heroSubtitle}>{t("hero.subtitle")}</Typography>
            <Typography sx={styles.heroDescription}>{t("hero.description")}</Typography>
          </Box>
        </Container>
        <Box sx={styles.heroBottomGradient} />
      </Box>

      <Box sx={{ background: "linear-gradient(to bottom, #040922 0%, #080f1e 30%, #040922 100%)" }}>
        <Container maxWidth="lg" sx={{ textAlign: "center", py: { xs: 10, md: 14 } }}>
          <Typography sx={styles.eyebrowSx}>{t("leadership.sectionEyebrow")}</Typography>
          <Typography component="h2" sx={styles.leadershipHeading}>
            {t("leadership.sectionHeading")}
          </Typography>
        </Container>

        <Box ref={ceoRef}>
          <CEOSpotlightSection inView={ceoInView} t={t} />
        </Box>

        <Box ref={leadershipRef}>
          {leaders.map((leader, i) => (
            <LeaderRow key={leader.name} leader={leader} index={i} inView={leadershipInView} />
          ))}
        </Box>

        <Container maxWidth="lg" sx={{ textAlign: "center", py: { xs: 10, md: 14 } }}>
          <Typography sx={styles.eyebrowSx}>{t("directors.sectionEyebrow")}</Typography>
          <Typography component="h2" sx={styles.directorsHeading}>
            {t("directors.sectionHeading")}
          </Typography>

          <Box ref={directorsRef}>
            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 3 }}>
              {directors.map((director, i) => (
                <DirectorCard key={director.name} director={director} index={i} inView={directorsInView} />
              ))}
            </Box>
          </Box>
        </Container>

        <Box ref={missionRef}>
          <MissionVisionValuesSection inView={missionInView} t={t} />
        </Box>
      </Box>
    </>
  );
};

const AboutPage = () => {
  const { t } = useTranslation("static_about");
  return (
    <StaticPageLayout
      title={t("meta.title")}
      description={t("meta.description")}
      canonicalUrl="https://powur.com/about"
      noTopSpacer
    >
      <AboutContent />
    </StaticPageLayout>
  );
};
export default AboutPage;
